/**
 * Render quality — how many pixels we actually render, and whether we multisample.
 *
 * ## The bug this fixes
 *
 * Cesium renders at the *browser recommended* resolution by default:
 *
 *   // CesiumWidget.js
 *   let pixelRatio = widget._useBrowserRecommendedResolution ? 1.0 : window.devicePixelRatio;
 *   pixelRatio *= widget._resolutionScale;
 *
 * `useBrowserRecommendedResolution` defaults to **true** and `resolutionScale` to
 * **1.0**, so the WebGL backing store is sized in CSS pixels. On any HiDPI screen
 * the scene is therefore rendered at 1/dpr² of the display's real resolution and
 * upscaled by the browser. Measured on a DPR-2 Mac: canvas 1080x937 backing store
 * for a 1080x937 CSS box — 25% of the pixels the display actually shows. Thin
 * geometry (the vario track above all) took the worst of it and looked "pixelled".
 *
 * ## Why there is no MSAA / FXAA here
 *
 * `contextOptions.webgl.antialias` is inert in this app — see ViewerComponent.js.
 * The real knobs are `scene.msaaSamples` (defaults to 1 = off) and
 * `postProcessStages.fxaa.enabled` (defaults to false).
 *
 * We deliberately leave **both off** and spend the budget on resolution instead.
 * At `resolutionScale === devicePixelRatio` the scene is already rendered 1:1 with
 * the physical pixels, so MSAA's marginal benefit is small — while its cost is not.
 * Measured on the 86 357-fix flight at 60x playback with the follow camera:
 *
 *   Apple M5 Max, DPR 2, 1280x813 CSS
 *     scale 1    msaa 1   1280x813     9.0 ms   111 fps
 *     scale 1    msaa 4   1280x813    12.2 ms    82 fps
 *     scale 1.5  msaa 1   1920x1219    8.7 ms   115 fps
 *     scale 2    msaa 1   2560x1626   12.0 ms    83 fps   <- shipped
 *     scale 2    msaa 4   2560x1626   28.9 ms    35 fps
 *     scale 3    msaa 1   3840x2439   17.9 ms    56 fps
 *
 *   Samsung Galaxy A54 (Mali-G68), DPR 2.8125, 384x694 CSS
 *     scale 1     msaa 1    384x694    16.8 ms   60 fps  (vsync-locked)
 *     scale 1     msaa 4    384x694    18.2 ms   55 fps
 *     scale 1.5   msaa 1    576x1041   16.8 ms   60 fps  (vsync-locked) <- shipped
 *     scale 1.5   msaa 4    576x1041   33.8 ms   30 fps
 *     scale 2.81  msaa 1   1080x1951   36.8 ms   27 fps
 *     scale 2.81  msaa 4   1080x1951  100.5 ms   10 fps
 *
 * Resolution is cheap, MSAA is expensive — on *both* GPUs, and dramatically so on
 * the mobile tiler (do not assume MSAA is cheap on tile-based mobile GPUs; it is
 * not on Mali-G68, where 4x samples cost 2-3x the frame time). `msaaSamples` is
 * therefore pinned to 1 explicitly rather than left to the default, because newer
 * CesiumJS releases changed that default to 4.
 *
 * ## Why mobile is capped below native
 *
 * Phones report a very high DPR (2.8125 on the A54) for a physically tiny screen.
 * With terrain and imagery fully loaded, the scale-1.5 and scale-2.8125 frames
 * differ by a mean of 3.85/255 (~1.5%) — while costing 60 fps vs 27 fps. Past
 * ~1.5 the extra pixels are not visible at that pixel density, so we do not pay
 * for them. Note also that the terrain tileset caps at zoom 15, so beyond a point
 * extra resolution only sharpens imagery, never the terrain silhouette.
 *
 * Billboards and labels need **no compensation** for any of this: they are sized
 * via `czm_metersPerPixel`, whose one-argument overload already multiplies by
 * `czm_pixelRatio`. Verified by measuring the pilot marker on-screen at three
 * different scales — 189 / 190 / 195 device px wide, i.e. unchanged.
 */

// Hard ceilings on resolutionScale. Never exceed the display's own pixel ratio —
// past that we would be supersampling, which costs 4x per step for nothing.
const DESKTOP_MAX_SCALE = 2;
const MOBILE_MAX_SCALE = 1.5;

// Pinned explicitly; see the header. 1 = off.
const MSAA_SAMPLES = 1;

// --- adaptive back-off ------------------------------------------------------
// The tables above cover an M5 Max and a mid-range Android. Weaker devices exist,
// so once a flight is on screen we measure and step down if needed.
//
// This MUST be reversible. A phone's frame cost is not a fixed property of the
// device — it collapses under thermal throttling and recovers minutes later.
// Measured on the A54 after sustained benchmarking while charging (thermal
// status MODERATE, skin 44.6 °C), at the shipping config, four samples ~6 s
// apart: 48.6 → 38.2 → 21.8 → 17.4 ms, i.e. back to its unthrottled 16.8 ms.
// A one-shot downgrade would have pinned a hot phone to a low resolution for the
// whole session — and this app is used outdoors, in summer, in the sun. So we
// require two consecutive over-budget samples before dropping a rung, hand the
// rung back when it is comfortably fast again, and re-evaluate on every load.
const FRAME_BUDGET_MS = 22; // ~45 fps; above this (twice) we drop a rung
const RECOVER_MS = 13; // ~77 fps; below this (twice) we give one back
const CONFIRM_SAMPLES = 2; // consecutive samples that must agree before moving
const STEP = 0.75; // multiplier per rung
const MIN_SCALE = 1.0; // never go below CSS resolution
const MAX_STEPS = 2;
const MIN_FACTOR = Math.pow(STEP, MAX_STEPS);
const SAMPLE_FRAMES = 90;
const WARMUP_FRAMES = 10; // discard, so tile uploads don't count
const TILE_SETTLE_TIMEOUT_MS = 15000;

/**
 * Coarse pointer + touch is a better proxy than UA sniffing: it catches phones and
 * tablets (which have the same "tiny physical pixels" property) and not a laptop
 * with a touchscreen driven by a trackpad.
 */
function isHandheld() {
  if (typeof window.matchMedia !== "function") return false;
  return (
    window.matchMedia("(pointer: coarse)").matches &&
    (navigator.maxTouchPoints || 0) > 0
  );
}

/** The scale we want before any adaptive back-off. */
function targetScale() {
  const dpr = window.devicePixelRatio || 1;
  return Math.min(dpr, isHandheld() ? MOBILE_MAX_SCALE : DESKTOP_MAX_SCALE);
}

function state(viewer) {
  if (!viewer.__renderQuality) viewer.__renderQuality = { factor: 1, running: false };
  return viewer.__renderQuality;
}

function alive(viewer) {
  return viewer && !(viewer.isDestroyed && viewer.isDestroyed());
}

/** Re-apply the target (keeping any back-off already applied). */
function applyScale(viewer) {
  const s = state(viewer);
  viewer.resolutionScale = Math.max(MIN_SCALE, targetScale() * s.factor);
}

/**
 * Dragging the window to a monitor with a different pixel ratio changes what
 * "native" means. Cesium re-reads devicePixelRatio on resize, but only when
 * `useBrowserRecommendedResolution` is false — we leave it true and drive
 * `resolutionScale` directly, so we have to watch for the change ourselves.
 */
function watchDevicePixelRatio(viewer) {
  // Not dead code: some browsers stop delivering `change` on a MediaQueryList
  // that nothing references, so we hold the current one.
  let held;
  const onChange = () => {
    if (!alive(viewer)) return;
    applyScale(viewer);
    attach();
  };
  const attach = () => {
    if (typeof window.matchMedia !== "function") return;
    held = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    // `once` + re-attach, because the query itself has to be rebuilt each time.
    // Safari < 14 has no addEventListener on MediaQueryList — fall back to the
    // deprecated addListener, and simply do without if neither exists.
    if (typeof held.addEventListener === "function") {
      held.addEventListener("change", onChange, { once: true });
    } else if (typeof held.addListener === "function") {
      held.addListener(function once() {
        held.removeListener(once);
        onChange();
      });
    }
  };
  attach();
}

/**
 * Set the render resolution and antialiasing. Call once, right after the viewer
 * exists — before anything is drawn.
 *
 * This runs inside viewer construction, so it must never throw: a failure here
 * would take down app initialisation for what is a purely cosmetic feature.
 * Worst case we fall back to Cesium's default (CSS-resolution) rendering.
 */
export function applyRenderQuality(viewer) {
  if (!alive(viewer)) return;
  try {
    applyScale(viewer);
    viewer.scene.msaaSamples = MSAA_SAMPLES;
    watchDevicePixelRatio(viewer);
  } catch (e) {
    console.warn("[renderQuality] disabled — falling back to Cesium defaults", e);
  }
}

/** Median frame interval over a sample, or null if the tab went away mid-run. */
function sampleFrameMs() {
  return new Promise((resolve) => {
    const intervals = [];
    let last = performance.now();
    let n = 0;
    const step = () => {
      if (document.visibilityState !== "visible") return resolve(null);
      const now = performance.now();
      if (n >= WARMUP_FRAMES) intervals.push(now - last);
      last = now;
      if (++n < WARMUP_FRAMES + SAMPLE_FRAMES) requestAnimationFrame(step);
      else {
        intervals.sort((a, b) => a - b);
        resolve(intervals[intervals.length >> 1]);
      }
    };
    requestAnimationFrame(step);
  });
}

/** Resolve once terrain/imagery has stopped streaming, so we measure steady state. */
function waitForTiles(viewer) {
  return new Promise((resolve) => {
    const t0 = performance.now();
    const check = () => {
      if (!alive(viewer)) return resolve();
      if (viewer.scene.globe.tilesLoaded) return resolve();
      if (performance.now() - t0 > TILE_SETTLE_TIMEOUT_MS) return resolve();
      setTimeout(check, 250);
    };
    check();
  });
}

function move(viewer, samples, nextFactor, why) {
  const s = state(viewer);
  const before = viewer.resolutionScale;
  const previousFactor = s.factor;
  s.factor = nextFactor;
  applyScale(viewer);
  if (viewer.resolutionScale === before) {
    // Clamped by MIN_SCALE, so the rung bought nothing — don't bank the change,
    // or recovery would need extra steps to undo a move that never happened.
    s.factor = previousFactor;
    return;
  }
  console.info(
    `[renderQuality] ${samples.map((m) => m.toFixed(1)).join(" / ")} ms/frame at scale ` +
      `${before.toFixed(2)} — ${why}, now ${viewer.resolutionScale.toFixed(2)}`
  );
}

/**
 * Measure the real frame cost with a flight on screen and move the resolution a
 * rung if this device cannot afford the target — or hand a rung back once it
 * can again. Call whenever a flight is loaded; concurrent calls are ignored.
 */
export async function calibrateRenderQuality(viewer) {
  if (!alive(viewer)) return;
  const s = state(viewer);
  if (s.running) return;
  s.running = true;

  try {
    await waitForTiles(viewer);

    const samples = [];
    for (let i = 0; i < CONFIRM_SAMPLES; i++) {
      if (!alive(viewer)) return;
      const median = await sampleFrameMs();
      if (median == null) return; // tab hidden — no verdict
      samples.push(median);
    }
    if (!alive(viewer)) return;

    // Both samples must agree, so a single hitch (or a passing thermal dip)
    // cannot move us. Anything in between leaves the resolution alone.
    if (samples.every((m) => m > FRAME_BUDGET_MS) && s.factor > MIN_FACTOR) {
      move(viewer, samples, s.factor * STEP, `over the ${FRAME_BUDGET_MS} ms budget`);
    } else if (samples.every((m) => m < RECOVER_MS) && s.factor < 1) {
      move(viewer, samples, Math.min(1, s.factor / STEP), "comfortably under budget");
    }
  } catch (e) {
    // Called fire-and-forget from CreateDataSource; never surface as an
    // unhandled rejection, and never take the resolution with it.
    console.warn("[renderQuality] calibration skipped", e);
  } finally {
    s.running = false;
  }
}
